import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export default function LandingPagePublic() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/landing/${slug}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Page not found"));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
        utm_term: params.get("utm_term") || "",
        utm_content: params.get("utm_content") || "",
      };
      const { data: r } = await axios.post(`${API}/public/landing/${slug}/lead`, payload);
      setDone(r);
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-ink text-bone flex items-center justify-center px-6">
        <div className="border border-hairline p-10 max-w-md text-center bg-surface">
          <h1 className="font-display text-2xl font-bold tracking-tightest">{error}</h1>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const a = data.article;

  return (
    <div className="min-h-screen bg-ink text-bone">
      <header className="border-b border-hairline bg-surface/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-3">
          <div className="w-7 h-7 bg-safety flex items-center justify-center">
            <span className="font-display font-black text-white text-xs">L</span>
          </div>
          <div>
            <div className="font-display font-bold tracking-tightest text-sm leading-none">WiseWit Solutions</div>
            <div className="label-mono text-[10px] text-bone/50 leading-none mt-1">
              AI for industrial operations
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {data.persona && (
          <div className="label-mono text-safety">// for {data.persona.industry_label || data.persona.name}</div>
        )}
        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tightest mt-3" data-testid="landing-title">
          {a.title}
        </h1>
        {a.cover_image && (
          <img src={a.cover_image} alt="" className="mt-6 w-full border border-hairline" />
        )}
        <article className="mt-8 prose prose-lg max-w-none text-bone/85 whitespace-pre-wrap font-sans leading-relaxed" data-testid="landing-body">
          {a.body}
        </article>
        <div className="mt-6 flex flex-wrap gap-2">
          {(a.hashtags || []).map((h, i) => (
            <Badge key={i} className="rounded-none bg-klein text-white label-mono">{h}</Badge>
          ))}
        </div>

        {/* Lead form */}
        <section id="talk" className="mt-16 border-t-2 border-safety pt-10" data-testid="landing-lead-form">
          {done ? (
            <div className="border border-safety bg-surface p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-safety mx-auto" />
              <h2 className="font-display text-3xl font-black tracking-tightest mt-4">
                We're on it.
              </h2>
              <p className="mt-3 text-bone/70">
                Thanks, {form.name?.split(" ")[0] || "there"}. A real human from WiseWit will reply
                within 24 hours. {done.intent_score ? `(Your intent score: ${done.intent_score}/100 — we'll prioritise high-intent first.)` : ""}
              </p>
            </div>
          ) : (
            <>
              <div className="label-mono text-safety">// next step</div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tightest mt-2">
                {data.cta_label || "Talk to WiseWit about AI for your operations"}
              </h2>
              <p className="mt-3 text-bone/60 max-w-xl">
                Tell us about your operations. We reply within one business day with a concrete
                next step — no slide decks, no chasing.
              </p>
              <form onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-4">
                <Field label="Your name *" k="name" form={form} setForm={setForm} required testid="lead-name-input" />
                <Field label="Work email *" k="email" type="email" form={form} setForm={setForm} required testid="lead-email-input" />
                <Field label="Company" k="company" form={form} setForm={setForm} testid="lead-company-input" />
                <Field label="Your role" k="role" form={form} setForm={setForm} placeholder="e.g. CTO, Ops Manager" testid="lead-role-input" />
                <div className="md:col-span-2">
                  <Label className="label-mono text-bone/70">What are you trying to solve?</Label>
                  <Textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="A short note helps us reply faster."
                    className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
                    data-testid="lead-message-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit-btn"
                    className="w-full rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-12"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Talk to WiseWit
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>

        <footer className="mt-16 pt-6 border-t border-hairline label-mono text-bone/40 text-xs">
          © 2026 WiseWit Solutions Private Limited · Drafted with Lingent
        </footer>
      </main>
    </div>
  );
}

const Field = ({ label, k, type = "text", form, setForm, required, placeholder, testid }) => (
  <div>
    <Label className="label-mono text-bone/70">{label}</Label>
    <Input
      type={type}
      value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
    />
  </div>
);
